"use client";

import { useState } from "react";

type Status = "idle" | "loading" | "success" | "error";

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 10,
  border: "1px solid var(--line-strong)",
  background: "var(--paper-3)",
  color: "var(--ink)",
  fontSize: 14,
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 12.5,
  color: "var(--ink-mute)",
  marginBottom: 6,
  fontFamily: "var(--font-jetbrains-mono), monospace",
};

const req = <span style={{ color: "#f87171" }}>*</span>;

export function EmailCourseForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;

    const firstName    = (form.elements.namedItem("firstName")    as HTMLInputElement).value.trim();
    const lastName     = (form.elements.namedItem("lastName")     as HTMLInputElement).value.trim();
    const email        = (form.elements.namedItem("email")        as HTMLInputElement).value.trim();
    const title        = (form.elements.namedItem("title")        as HTMLInputElement).value.trim();
    const organisation = (form.elements.namedItem("organisation") as HTMLInputElement).value.trim();
    const country      = (form.elements.namedItem("country")      as HTMLInputElement).value.trim();

    if (!firstName) { setError("Please enter your first name."); return; }
    if (!email)     { setError("Please enter your email address."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setStatus("loading");
    setError("");

    try {
      const res = await fetch("/api/email-course/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName, lastName, email, title, organisation, country }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "Subscription failed.");
      }

      setStatus("success");
      form.reset();
    } catch (err) {
      setStatus("error");
      setError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    }
  }

  if (status === "success") {
    return (
      <div role="status" style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 12, padding: "24px 0" }}>
        <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 56, height: 56, borderRadius: "50%", background: "rgba(34,197,94,0.12)", color: "var(--green)" }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <p style={{ fontSize: 17, fontWeight: 700, color: "var(--ink)", margin: 0 }}>You&rsquo;re in!</p>
        <p style={{ fontSize: 14, color: "var(--ink-soft)", margin: 0, maxWidth: 280 }}>
          Day 1 is on its way. Check your inbox — and your <strong style={{ color: "var(--ink)" }}>Promotions</strong> or <strong style={{ color: "var(--ink)" }}>Spam</strong> folder just in case.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* First + Last name row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label htmlFor="ec-firstName" style={labelStyle}>First Name {req}</label>
          <input
            id="ec-firstName"
            name="firstName"
            type="text"
            placeholder="Jane"
            required
            autoComplete="given-name"
            style={inputStyle}
          />
        </div>
        <div>
          <label htmlFor="ec-lastName" style={labelStyle}>Last Name</label>
          <input
            id="ec-lastName"
            name="lastName"
            type="text"
            placeholder="Doe"
            autoComplete="family-name"
            style={inputStyle}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="ec-email" style={labelStyle}>Work Email {req}</label>
        <input
          id="ec-email"
          name="email"
          type="email"
          placeholder="jane@company.com"
          required
          autoComplete="email"
          style={inputStyle}
        />
      </div>

      {/* Title */}
      <div>
        <label htmlFor="ec-title" style={labelStyle}>Job Title</label>
        <input
          id="ec-title"
          name="title"
          type="text"
          placeholder="e.g. Supply Chain Manager"
          autoComplete="organization-title"
          style={inputStyle}
        />
      </div>

      {/* Organisation */}
      <div>
        <label htmlFor="ec-organisation" style={labelStyle}>Organisation</label>
        <input
          id="ec-organisation"
          name="organisation"
          type="text"
          placeholder="Company or consulting firm"
          autoComplete="organization"
          style={inputStyle}
        />
      </div>

      {/* Country */}
      <div>
        <label htmlFor="ec-country" style={labelStyle}>Country</label>
        <input
          id="ec-country"
          name="country"
          type="text"
          placeholder="e.g. United Kingdom"
          autoComplete="country-name"
          style={inputStyle}
        />
      </div>

      {status === "error" && (
        <p role="alert" style={{ fontSize: 13, color: "#f87171", margin: 0, textAlign: "center" }}>{error}</p>
      )}

      <button
        type="submit"
        className="btn btn-primary btn-lg"
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 4 }}
        disabled={status === "loading"}
      >
        {status === "loading" ? "Signing up…" : "Sign Up for Free →"}
      </button>

      <p style={{ fontSize: 12, color: "var(--ink-mute)", textAlign: "center", margin: 0 }}>
        Free. No credit card. Unsubscribe any time.
      </p>
    </form>
  );
}
